import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { TriangleAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Stops one bad render from blanking the whole app.
 *
 * React unmounts the entire tree when a render throws and nothing catches it,
 * so before this a single panel handed a malformed payload — a field the API
 * stopped sending, say — left the user staring at a white page with no way
 * back. This keeps the shell and offers a retry.
 */
class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Route render failed:', error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="mx-auto max-w-2xl px-4 py-16 md:px-6">
        <div className="flex flex-col items-center rounded-lg border border-border bg-card px-6 py-14 text-center">
          <TriangleAlert className="mb-3 size-8 text-warning" />
          <h1 className="font-display text-lg font-semibold">Esta pantalla no se pudo mostrar</h1>
          <p className="mt-1 mb-6 text-sm text-muted-foreground">
            El resto de la aplicación sigue funcionando. Si vuelve a pasar, avísanos con lo que
            estabas haciendo.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <Button onClick={() => this.setState({ error: null })}>Reintentar</Button>
            <Button variant="outline" onClick={() => window.location.assign('/app/patients')}>
              Ir a pacientes
            </Button>
          </div>
          {import.meta.env.DEV && (
            <pre className="mt-6 max-w-full overflow-x-auto rounded-control bg-muted p-3 text-left text-xs text-muted-foreground">
              {this.state.error.message}
            </pre>
          )}
        </div>
      </div>
    );
  }
}

export default RouteErrorBoundary;
